import { type HighnoteTransactionEvent, HighnoteTransactionStatus } from 'API';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { ReactComponent as BackArrow } from 'assets/BackArrow.svg';
import { fileAccessService } from 'app/services/fileAccess.service';
import { useAccountType } from 'app/hooks/useAccountType';
import { useCanEditTransaction } from 'app/hooks/transactions/useCanEditTransaction';
import { useTransactionReceipts } from 'app/hooks/useTransactionReceipts';
import { useFuelCapture } from 'app/hooks/useFuelCapture';
import { useAuthorizationDate } from 'app/hooks/transactions/useAuthorizationDate';
import { formatDateInEastern } from 'util/dateUtils';
import { useCodingSystemConfig } from 'app/hooks/transactions/useCodingSystemConfig';
import { useCodingDisplayValues } from 'app/hooks/transactions/useCodingDisplayValues';
import { MobileCodingFieldList } from 'app/pages/mobile/components/MobileCodingFieldList';
import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { BACKGROUND_SIZES } from 'app/pages/mobile/utils/constants';
import { formatCurrency } from 'app/shared-components/util/Currency';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MobileReceiptPreview from './MobileReceiptPreview';
import TransactionField from './TransactionField';
import MessageToCardholder from 'app/pages/overview/tabs/transactions/MessageToCardholder';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import {
  CODING_COLUMNS_BY_TYPE,
  resolveDisplayCodingType,
  type CodingColumnId,
} from 'app/util/columnVisibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ReturnedStatusChip,
  shouldShowReturnedStatus,
  getReturnedBannerKey,
} from 'app/pages/overview/tabs/transactions/ReturnedStatusChip';
import { getFlexcodeStatusText } from 'app/pages/overview/tabs/transactions/FlexTransactions/FlexStatusChip';
import { useGroupApprovalsEnabled } from 'app/hooks/transactions/useGroupApprovalsEnabled';
import {
  agaveLabelKeyFor,
  getAgaveStatusText,
} from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveStatusChip';
import { labelKeyFor } from 'util/workflowStatus';
import { sentryMask } from 'sentryReplayPrivacy';
import { useApprovalLevels } from 'app/hooks/transactions/useApprovalLevels';

interface MobileTransactionDetailsProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  transaction?: HighnoteTransactionEvent;
}

const MobileTransactionDetails = ({ open, onClose, onEdit, transaction }: MobileTransactionDetailsProps) => {
  const { t } = useTranslation();
  const [receiptUrls, setReceiptUrls] = useState<string[]>([]);
  const [receiptError, setReceiptError] = useState<Error | null>(null);
  const { hasErpCodingUI, isConstructionType, isFlexCodingEnabled } = useAccountType();
  const { isFuelCaptureEnabled, isFuelTransactionFn, isMCC5541Required, isMCC5542Required } =
    useFuelCapture();
  const isFuelTransaction = isFuelTransactionFn(transaction?.merchantCategoryCode);

  // Column visibility - single source of truth for which fields to show
  const { isVisible, getLabel } = useCodingSystemConfig();
  const getCodingDisplayValue = useCodingDisplayValues(transaction as Record<string, unknown> | undefined);

  // Fetch receipts using the hook (uses ReceiptUpload and TransactionToReceiptUpload)
  const { receipts } = useTransactionReceipts(transaction?.id, undefined);

  // SEE-1091 — when equipment coding is on, show only the resolved type's coding
  // rows (matches the edit flow + Figma). Flag off => full flat list as before.
  const { 'equipment.enableEquipmentCoding': isEquipmentCodingEnabled } = useIsEnabled([
    { featureName: 'equipment', flagName: 'enableEquipmentCoding' },
  ]);
  const codingTypeColumns = useMemo<CodingColumnId[] | undefined>(() => {
    if (!(hasErpCodingUI && isConstructionType && isEquipmentCodingEnabled)) return undefined;
    const type = resolveDisplayCodingType(transaction);
    if (!type) return [];
    return [...CODING_COLUMNS_BY_TYPE[type], 'vendor' as CodingColumnId].filter((id) => isVisible(id));
  }, [hasErpCodingUI, isConstructionType, isEquipmentCodingEnabled, transaction, isVisible]);

  const isTransactionPending = useMemo(
    () => transaction?.status === HighnoteTransactionStatus.APPROVED,
    [transaction?.status]
  );
  const { authorizationDate } = useAuthorizationDate(transaction);

  const useAgave = hasErpCodingUI && isConstructionType;
  const useFlex = isFlexCodingEnabled;
  const connectedApprovalLevels = useApprovalLevels('connected');
  const flexApprovalLevels = useApprovalLevels('flex');
  const groupApprovalsEnabled = useGroupApprovalsEnabled();

  const canEdit = useCanEditTransaction(useFlex && !useAgave ? 'flex' : 'connected');
  const isTransactionLocked = !canEdit(transaction);

  // Collect receipt paths from ReceiptUpload array (new system only)
  const receiptPathsToLoad = useMemo(() => {
    const paths: string[] = [];

    // Add receipts from the ReceiptUpload array
    receipts.forEach((receipt) => {
      if (receipt?.document && receipt.document.trim().length > 0) {
        paths.push(receipt.document);
      }
    });

    return paths;
  }, [receipts]);

  useEffect(() => {
    const loadReceiptUrls = async () => {
      if (receiptPathsToLoad.length === 0) {
        setReceiptUrls([]);
        setReceiptError(null);
        return;
      }

      try {
        setReceiptError(null);
        // Load all receipt URLs in parallel
        const urls = await Promise.all(
          receiptPathsToLoad.map((path) => fileAccessService.getDownloadUrl(path))
        );
        setReceiptUrls(urls);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading receipts:', error);
        setReceiptError(error instanceof Error ? error : new Error('Failed to load receipts'));
        setReceiptUrls([]);
      }
    };

    if (open) {
      loadReceiptUrls();
    }
  }, [receiptPathsToLoad, open]);

  if (!open) return null;

  const transactionContainerStyle = {
    px: 2,
    py: 3,
    gap: 2,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 4,
  };

  const fieldStyle = {
    borderBottom: '1px solid',
    borderColor: 'rgba(255, 255, 255, 0.2)',
    pb: 2,
    '&:last-child': {
      borderBottom: 'none',
      pb: 0,
    },
  };

  const fieldStyleCodes = {
    ...fieldStyle,
    minHeight: '64px',
  };

  if (!transaction) {
    return (
      <MobilePageContent
        title={t('mobileTransactionDetails.title')}
        onBack={onClose}
        transparentHeight={BACKGROUND_SIZES.FULL}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      </MobilePageContent>
    );
  }

  // Status row mirrors the web sidesheet: a red "Returned" chip when returned,
  // otherwise the underlying review status. Mobile is always the owner's view.
  const flexStatus = getFlexcodeStatusText({
    status: transaction.flexCodeTransactionStatus,
    workflowStatus: transaction.workflowStatus,
    transaction,
    approvalLevels: flexApprovalLevels,
  });
  const agaveStatus = getAgaveStatusText({
    erpApprovalStatus: transaction.erpApprovalStatus,
    agaveExpenseId: transaction.agaveExpenseId,
    erpBypassStatus: transaction.erpBypassStatus,
    transaction,
    approvalLevels: connectedApprovalLevels,
  });
  const reviewStatusKey = useAgave ? agaveStatus : useFlex ? flexStatus : null;
  const reviewStatusLabelKey = !reviewStatusKey
    ? null
    : useAgave
    ? agaveLabelKeyFor(reviewStatusKey, transaction, connectedApprovalLevels, groupApprovalsEnabled)
    : labelKeyFor(reviewStatusKey, 'flex', flexApprovalLevels, groupApprovalsEnabled);
  const statusValue = shouldShowReturnedStatus(transaction) ? (
    <ReturnedStatusChip returnedStatus={transaction.returnedStatus} isCardholder />
  ) : reviewStatusLabelKey ? (
    t(`transactionsPage.tableRows.${reviewStatusLabelKey}`)
  ) : (
    t('mobileTransactionDetails.noStatus', '--')
  );

  const bannerKey = getReturnedBannerKey(transaction);

  return (
    <MobilePageContent
      title=""
      transparentHeight={BACKGROUND_SIZES.FULL}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 5, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={onClose}
              aria-label="Back"
              sx={{
                color: 'white',
                padding: 0,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <BackArrow style={{ height: '16px', width: 'auto' }} />
            </IconButton>
            <Typography variant="subtitle1" sx={{ color: 'common.white', fontWeight: 'bold' }}>
              {t('mobileTransactionDetails.title', 'Transaction Details')}
            </Typography>
          </Box>
          {!isTransactionLocked && (
            <IconButton
              onClick={onEdit}
              aria-label="Edit"
              sx={{
                color: 'white',
                padding: 0,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <EditIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          )}
        </Box>

        {bannerKey && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'common.white', mt: '2px' }} />
            <Typography sx={{ fontSize: '0.875rem', color: 'common.white' }}>
              {bannerKey === 'returned'
                ? t(
                    'mobileTransactionDetails.returnedBanner',
                    'This transaction was returned. Please review comments below and edit to resubmit.'
                  )
                : t(
                    'mobileTransactionDetails.resubmittedBanner',
                    'This transaction has been resubmitted and is pending review.'
                  )}
            </Typography>
          </Box>
        )}

        {/* Admin messages — own collapsible card above the details (returned-txn review). */}
        {transaction.transactionId && (
          <Box sx={{ mb: 2 }}>
            <MessageToCardholder
              transactionId={transaction.transactionId}
              canReturn={false}
              variant="mobile"
            />
          </Box>
        )}

        {/* Card Details / Amount */}
        <Box
          sx={{
            ...transactionContainerStyle,
            mb: 4,
          }}
        >
          <TransactionField
            label={t('mobileTransactionDetails.card')}
            value={
              transaction.card ? (
                <>
                  <span {...sentryMask}>{transaction.card.name || ''}</span>
                  {` - ${transaction.card.last4}`}
                </>
              ) : (
                ''
              )
            }
            sx={fieldStyle}
            invertColor
          />
          <TransactionField
            label={t('mobileTransactionDetails.amount')}
            value={formatCurrency({
              value: transaction.approvedAmount?.value || 0,
            })}
            sx={fieldStyle}
            invertColor
          />
          <TransactionField
            label={t('mobileTransactionDetails.transactionDate')}
            value={
              authorizationDate
                ? formatDateInEastern(authorizationDate)
                : t('mobileTransactionDetails.noDate')
            }
            sx={fieldStyle}
            invertColor
          />
          <TransactionField
            label={t('mobileTransactionDetails.postedDate')}
            value={
              isTransactionPending
                ? t('N/A')
                : transaction.eventDate
                ? formatDateInEastern(transaction.eventDate)
                : t('mobileTransactionDetails.noDate')
            }
            sx={fieldStyle}
            invertColor
          />
          <TransactionField
            label={t('mobileTransactionDetails.status', 'Status')}
            value={statusValue}
            sx={fieldStyle}
            invertColor
          />
          <TransactionField
            label={t('mobileTransactionDetails.merchant')}
            value={`${transaction.merchantName || t('mobileTransactionDetails.unknown')}`}
            sx={fieldStyle}
            invertColor
          />
          {(receiptPathsToLoad.length > 0 || receipts.length > 0) && (
            <Box sx={{ mt: 2 }}>
              {receiptError ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    border: '1px dashed',
                    borderColor: 'error.main',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                    {t('mobileTransactionDetails.errorLoadingReceipt') || 'Error loading receipt'}
                  </Typography>
                </Box>
              ) : receiptUrls.length > 0 ? (
                <MobileReceiptPreview
                  viewOnly
                  scopeId={`transaction-${transaction.id}`}
                  receiptUrls={receiptUrls}
                  onError={(error) => {
                    // eslint-disable-next-line no-console
                    console.error('Error displaying receipt:', error);
                    setReceiptError(error);
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Typography variant="subtitle1" sx={{ color: 'common.white', fontWeight: 'bold', mt: 3, mb: 1 }}>
          {t('mobileTransactionDetails.projectCodes', 'Project Codes')}
        </Typography>
        <Box
          sx={{
            ...transactionContainerStyle,
          }}
        >
          <MobileCodingFieldList
            mode="readonly"
            isVisible={isVisible}
            getLabel={getLabel}
            orderedColumnIds={codingTypeColumns}
            displayValues={getCodingDisplayValue}
            fieldSx={fieldStyleCodes}
            invertColor
          />
          <TransactionField
            label={t('mobileTransactionDetails.notes')}
            value={<span {...sentryMask}>{transaction.note || ''}</span>}
            sx={fieldStyle}
            invertColor
          />
        </Box>
        {/* Fuel Capture Box - Only show when fuel capture is enabled */}
        {isFuelCaptureEnabled && isFuelTransaction && (isMCC5541Required || isMCC5542Required) && (
          <>
            <Typography variant="subtitle1" sx={{ color: 'common.white', fontWeight: 'bold', mt: 3, mb: 1 }}>
              {t('mobileTransactionDetails.fuelCapture', 'Fuel Capture')}
            </Typography>
            <Box
              sx={{
                ...transactionContainerStyle,
              }}
            >
              <TransactionField
                label={t('mobileTransactionDetails.vehicleId', 'Vehicle ID')}
                value={transaction.fuelVehicleId || '--'}
                sx={fieldStyle}
                invertColor
              />
              <TransactionField
                label={t('mobileTransactionDetails.odometerReading', 'Odometer')}
                value={
                  transaction.fuelOdometerReading != null
                    ? `${transaction.fuelOdometerReading.toString()} ${t('miles', 'miles')}`
                    : '--'
                }
                sx={fieldStyle}
                invertColor
              />
              <TransactionField
                label={t('mobileTransactionDetails.gallons', 'Gallons')}
                value={
                  transaction.fuelGallons != null
                    ? `${(transaction.fuelGallons / 1000).toFixed(3)} ${t('gal', 'gal')}`
                    : '--'
                }
                sx={fieldStyle}
                invertColor
              />
              <TransactionField
                label={t('mobileTransactionDetails.costPerGallon', 'Cost Per Gallon')}
                value={
                  transaction.fuelCostPerGallon != null
                    ? `${formatCurrency({ value: transaction.fuelCostPerGallon })}${t('perGal', '/gal')}`
                    : '--'
                }
                sx={fieldStyle}
                invertColor
              />
            </Box>
          </>
        )}
      </Box>
    </MobilePageContent>
  );
};

export default MobileTransactionDetails;
